import React, { useEffect, useMemo, useState } from 'react';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const fmt = value =>
  '\u20a6' + Number(value || 0).toLocaleString('en-NG');

const roleLabel = role =>
  String(role || '')
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const statusStyle = status => {
  const styles = {
    planned: { background:'#EDF4FF', color:'#1E4FAD' },
    in_progress: { background:'#FFF3DC', color:'#8B5E0A' },
    completed: { background:'#E8F5EB', color:'#2E6B3A' },
    cancelled: { background:'#FDEAEA', color:'#8F2E24' },
    missed: { background:'#FDEAEA', color:'#8F2E24' }
  };
  return styles[status] || { background:'#F0F0F0', color:'#555' };
};

export default function SalesManagerDashboard() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [team, setTeam] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCustomer, setSavingCustomer] = useState(null);
  const [search, setSearch] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [showVisit, setShowVisit] = useState(false);
  const [error, setError] = useState('');

  const allowed = ['super_admin','sales_manager'].includes(user?.role);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [summary, members, customerData, visitData] = await Promise.all([
        get('/crm/manager-dashboard'),
        get('/crm/team-members'),
        get('/customers?limit=200'),
        get('/crm/visits')
      ]);

      setDashboard(summary);
      setTeam(members.data || []);
      setCustomers(customerData.data || []);
      setVisits(visitData.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allowed) load();
  }, [allowed]);

  const assignCustomer = async (customer, assignedTo) => {
    const owner = team.find(member => member.id === assignedTo);
    const ownerName = owner?.name || 'Unassigned';

    if (!window.confirm(
      `Assign ${customer.name} to ${ownerName}?`
    )) return;

    const reason = window.prompt(
      'Reason for this assignment or reassignment:',
      customer.assigned_to
        ? 'Customer reassigned by sales manager'
        : 'Initial customer assignment'
    );

    if (reason === null) return;

    setSavingCustomer(customer.id);
    try {
      await put('/crm/customers/' + customer.id + '/assign', {
        assigned_to: assignedTo || null,
        reason: reason.trim() || null
      });
      await load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingCustomer(null);
    }
  };

  const filteredCustomers = useMemo(() => {
    const term = search.trim().toLowerCase();

    return customers.filter(customer => {
      const matchesSearch =
        !term ||
        customer.name?.toLowerCase().includes(term) ||
        customer.phone?.toLowerCase().includes(term) ||
        customer.customer_code?.toLowerCase().includes(term);

      const matchesOwner =
        !ownerFilter ||
        customer.assigned_to === ownerFilter ||
        (ownerFilter === 'unassigned' && !customer.assigned_to);

      return matchesSearch && matchesOwner;
    });
  }, [customers, search, ownerFilter]);

  if (!allowed) {
    return (
      <div className="empty-state">
        <h3>Sales manager access required</h3>
      </div>
    );
  }

  if (loading) return <div className="spinner"></div>;

  if (error) {
    return (
      <div className="alert alert-error">
        {error}
        <button
          className="btn btn-sm btn-outline"
          style={{marginLeft:12}}
          onClick={load}
        >
          Retry
        </button>
      </div>
    );
  }

  const summary = dashboard?.summary || {};
  const performance = dashboard?.team || [];
  const pipeline = dashboard?.pipeline || [];

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Sales Manager Dashboard</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Team performance, customer ownership and field activity
          </p>
        </div>
        <div className="flex-center gap-sm">
          <button className="btn btn-outline" onClick={load}>
            Refresh
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowVisit(true)}
          >
            + Plan Field Visit
          </button>
        </div>
      </div>

      <div style={{
        display:'grid',
        gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',
        gap:12,
        marginBottom:20
      }}>
        <Stat label="Customers" value={summary.total_customers || 0} />
        <Stat
          label="Unassigned"
          value={summary.unassigned_customers || 0}
          danger={Number(summary.unassigned_customers || 0) > 0}
        />
        <Stat label="Leads" value={summary.leads || 0} />
        <Stat label="Confirmed" value={summary.confirmed || 0} />
        <Stat
          label="At Risk"
          value={summary.at_risk || 0}
          danger={Number(summary.at_risk || 0) > 0}
        />
        <Stat
          label="Unattributed Sales"
          value={fmt(dashboard?.unattributed_revenue_this_month)}
          danger={Number(dashboard?.unattributed_revenue_this_month || 0) > 0}
        />
      </div>

      <div className="card" style={{marginBottom:20}}>
        <div className="card-hdr">
          <span className="card-title">Team Performance This Month</span>
        </div>
        <div className="tbl-wrap" style={{border:0,borderRadius:0}}>
          <table>
            <thead>
              <tr>
                <th>Team Member</th>
                <th>Customers</th>
                <th>Interactions</th>
                <th>Overdue</th>
                <th>Visits</th>
                <th style={{textAlign:'right'}}>Booked</th>
                <th style={{textAlign:'right'}}>Qualified</th>
                <th style={{textAlign:'right'}}>Target</th>
                <th>Progress</th>
              </tr>
            </thead>
            <tbody>
              {performance.map(member => {
                const qualified = Number(member.sales_this_month || 0);
                const target = Number(member.target_this_month || 0);
                const progress = target > 0
                  ? Math.min(100, qualified / target * 100)
                  : 0;

                return (
                  <tr key={member.id}>
                    <td>
                      <div style={{fontWeight:600}}>{member.name}</div>
                      <div className="text-muted" style={{fontSize:10}}>
                        {roleLabel(member.role)}
                      </div>
                    </td>
                    <td>{member.assigned_customers}</td>
                    <td>{member.interactions_this_month}</td>
                    <td style={{
                      color:Number(member.overdue_follow_ups) > 0
                        ? '#8F2E24'
                        : '#555',
                      fontWeight:Number(member.overdue_follow_ups) > 0
                        ? 700
                        : 400
                    }}>
                      {member.overdue_follow_ups}
                    </td>
                    <td>{member.visits_completed}</td>
                    <td className="amt">
                      {fmt(member.booked_sales_this_month)}
                    </td>
                    <td className="amt">
                      {fmt(member.sales_this_month)}
                    </td>
                    <td className="amt">
                      {fmt(member.target_this_month)}
                    </td>
                    <td style={{minWidth:130}}>
                      <div style={{
                        height:7,
                        background:'#F2EFE7',
                        borderRadius:5,
                        overflow:'hidden'
                      }}>
                        <div style={{
                          width:progress + '%',
                          height:'100%',
                          background:progress >= 100 ? '#556B2F' : '#A8893D'
                        }} />
                      </div>
                      <div style={{fontSize:10,marginTop:3}}>
                        {progress.toFixed(0)}%
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{
        display:'grid',
        gridTemplateColumns:'minmax(260px,.7fr) minmax(420px,1.3fr)',
        gap:16,
        marginBottom:20
      }}>
        <div className="card">
          <div className="card-hdr">
            <span className="card-title">Pipeline</span>
          </div>
          <div style={{padding:16}}>
            {pipeline.length === 0 ? (
              <div className="text-muted text-sm">No pipeline records</div>
            ) : pipeline.map(stage => (
              <div
                key={stage.pipeline_stage}
                style={{
                  display:'flex',
                  justifyContent:'space-between',
                  padding:'9px 0',
                  borderBottom:'1px solid var(--border-light)'
                }}
              >
                <span style={{textTransform:'capitalize'}}>
                  {String(stage.pipeline_stage).replace(/_/g,' ')}
                </span>
                <strong>{stage.count}</strong>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-hdr">
            <span className="card-title">Field Visits</span>
            <span className="text-muted text-sm">{visits.length} total</span>
          </div>
          <div style={{maxHeight:330,overflowY:'auto'}}>
            {visits.length === 0 ? (
              <div style={{padding:18}} className="text-muted text-sm">
                No field visits planned yet
              </div>
            ) : visits.slice(0,20).map(visit => {
              const badge = statusStyle(visit.status);
              return (
                <div
                  key={visit.id}
                  style={{
                    padding:'11px 16px',
                    borderBottom:'1px solid var(--border-light)'
                  }}
                >
                  <div className="flex-between">
                    <div>
                      <div style={{fontWeight:600,fontSize:13}}>
                        {visit.customer_name}
                      </div>
                      <div className="text-muted" style={{fontSize:11}}>
                        {visit.assigned_to_name} · {visit.planned_date}
                        {visit.planned_time
                          ? ' at ' + String(visit.planned_time).slice(0,5)
                          : ''}
                      </div>
                    </div>
                    <span style={{
                      ...badge,
                      padding:'3px 8px',
                      borderRadius:20,
                      fontSize:9,
                      fontWeight:700,
                      textTransform:'uppercase'
                    }}>
                      {String(visit.status).replace('_',' ')}
                    </span>
                  </div>
                  <div style={{fontSize:12,marginTop:5,color:'#555'}}>
                    {visit.purpose}
                  </div>
                  {visit.outcome && (
                    <div style={{fontSize:11,marginTop:3,color:'#2E6B3A'}}>
                      Outcome: {visit.outcome}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-hdr">
          <span className="card-title">Customer Assignment</span>
          <span className="text-muted text-sm">
            {filteredCustomers.length} customer
            {filteredCustomers.length === 1 ? '' : 's'}
          </span>
        </div>

        <div className="search-bar" style={{margin:16}}>
          <input
            placeholder="Search name, phone or customer code..."
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
          <select
            value={ownerFilter}
            onChange={event => setOwnerFilter(event.target.value)}
          >
            <option value="">All owners</option>
            <option value="unassigned">Unassigned</option>
            {team.map(member => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
          </select>
        </div>

        <div className="tbl-wrap" style={{border:0,borderRadius:0}}>
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Phone</th>
                <th>Pipeline</th>
                <th>Current Owner</th>
                <th>Assign To</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map(customer => (
                <tr key={customer.id}>
                  <td>
                    <div style={{fontWeight:600}}>{customer.name}</div>
                    <div className="text-muted" style={{fontSize:10}}>
                      {customer.customer_code}
                    </div>
                  </td>
                  <td>{customer.phone || 'None'}</td>
                  <td style={{textTransform:'capitalize'}}>
                    {String(customer.pipeline_stage || 'customer')
                      .replace(/_/g,' ')}
                  </td>
                  <td>{customer.assigned_to_name || 'Unassigned'}</td>
                  <td>
                    <select
                      className="form-control"
                      style={{minWidth:190}}
                      value={customer.assigned_to || ''}
                      disabled={
                        savingCustomer === customer.id ||
                        (
                          String(customer.assigned_to_role || '') ===
                            'super_admin' &&
                          user?.role !== 'super_admin'
                        )
                      }
                      title={
                        String(customer.assigned_to_role || '') ===
                          'super_admin' &&
                        user?.role !== 'super_admin'
                          ? 'Managing Director ownership is protected'
                          : 'Assign customer'
                      }
                      onChange={event =>
                        assignCustomer(customer, event.target.value)
                      }
                    >
                      {customer.assigned_to &&
                        !team.some(
                          member => member.id === customer.assigned_to
                        ) && (
                          <option value={customer.assigned_to}>
                            {customer.assigned_to_name || 'Protected owner'}
                            {String(customer.assigned_to_role || '') ===
                              'super_admin'
                              ? ' (Managing Director)'
                              : ''}
                          </option>
                        )}
                      <option value="">Unassigned</option>
                      {team.map(member => (
                        <option key={member.id} value={member.id}>
                          {member.name} ({roleLabel(member.role)})
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showVisit && (
        <PlanVisitModal
          customers={customers}
          team={team}
          onClose={() => setShowVisit(false)}
          onSaved={async () => {
            setShowVisit(false);
            await load();
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, danger=false }) {
  return (
    <div style={{
      background:danger ? '#FDEAEA' : '#fff',
      border:'1px solid ' + (danger ? '#F5B8B8' : 'var(--border-light)'),
      borderRadius:10,
      padding:'14px 16px',
      boxShadow:'var(--shadow)'
    }}>
      <div style={{
        fontSize:9,
        color:danger ? '#8F2E24' : '#8A8A8A',
        fontWeight:700,
        textTransform:'uppercase',
        letterSpacing:'.08em'
      }}>
        {label}
      </div>
      <div style={{
        fontFamily:'Cormorant Garamond,serif',
        fontSize:'1.7rem',
        fontWeight:700,
        color:danger ? '#8F2E24' : '#3B2A21',
        marginTop:4
      }}>
        {value}
      </div>
    </div>
  );
}

function PlanVisitModal({ customers, team, onClose, onSaved }) {
  const [form, setForm] = useState({
    customer_id:'',
    assigned_to:'',
    planned_date:'',
    planned_time:'',
    purpose:''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!form.customer_id || !form.assigned_to ||
        !form.planned_date || !form.purpose.trim()) {
      setError('Customer, assignee, date and purpose are required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await post('/crm/visits', form);
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:520}}>
        <div className="modal-hdr">
          <span className="modal-title">Plan Field Visit</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            X
          </button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}

          <div className="form-group">
            <label className="form-label">Customer *</label>
            <select
              className="form-control"
              value={form.customer_id}
              onChange={event => setForm({
                ...form,
                customer_id:event.target.value
              })}
            >
              <option value="">Select customer</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Assign To *</label>
            <select
              className="form-control"
              value={form.assigned_to}
              onChange={event => setForm({
                ...form,
                assigned_to:event.target.value
              })}
            >
              <option value="">Select sales employee</option>
              {team.map(member => (
                <option key={member.id} value={member.id}>
                  {member.name} ({roleLabel(member.role)})
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Visit Date *</label>
              <input
                type="date"
                className="form-control"
                value={form.planned_date}
                onChange={event => setForm({
                  ...form,
                  planned_date:event.target.value
                })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Time</label>
              <input
                type="time"
                className="form-control"
                value={form.planned_time}
                onChange={event => setForm({
                  ...form,
                  planned_time:event.target.value
                })}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Purpose *</label>
            <textarea
              className="form-control"
              rows={4}
              placeholder="What should be achieved during this visit?"
              value={form.purpose}
              onChange={event => setForm({
                ...form,
                purpose:event.target.value
              })}
            />
          </div>
        </div>

        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={saving}
          >
            {saving ? 'Scheduling...' : 'Schedule Visit'}
          </button>
        </div>
      </div>
    </div>
  );
}
